import { useState, useEffect } from 'react';
import { RevealBox, SectionLabel, SectionTitle } from './UI';

const deadlines = [
  {
    university: 'Amity University',
    program: 'MBA / MCA / MA',
    date: new Date('2025-07-31T23:59:59'),
    badge: 'NAAC A++',
    color: '#1a3a8f',
    seats: 42,
    totalSeats: 120,
  },
  {
    university: 'DY Patil University',
    program: 'MBA / BBA / BCA',
    date: new Date('2025-08-15T23:59:59'),
    badge: 'NAAC A++',
    color: '#7b1f2e',
    seats: 18,
    totalSeats: 80,
  },
  {
    university: 'NMIMS University',
    program: 'MBA / BBA / MCom',
    date: new Date('2025-07-20T23:59:59'),
    badge: 'NAAC A+',
    color: '#1a5c3a',
    seats: 11,
    totalSeats: 60,
  },
  {
    university: 'Manipal University',
    program: 'MBA / MCA / MA / BCA',
    date: new Date('2025-08-31T23:59:59'),
    badge: 'NAAC A++',
    color: '#4a3a1a',
    seats: 55,
    totalSeats: 150,
  },
  {
    university: 'Chandigarh University',
    program: 'MBA / MCA / BBA / BCA',
    date: new Date('2025-09-15T23:59:59'),
    badge: 'NAAC A+',
    color: '#2a1a5c',
    seats: 67,
    totalSeats: 200,
  },
  {
    university: 'Professional Diplomas',
    program: 'All Certification Courses',
    date: new Date('2025-07-10T23:59:59'),
    badge: 'Rolling Admissions',
    color: '#1a4a3a',
    seats: 8,
    totalSeats: 40,
  },
];

function useCountdown(targetDate) {
  const [time, setTime] = useState(() => {
    const diff = targetDate - new Date();

    if (diff <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: true,
      };
    }

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      expired: false,
    };
  });

  useEffect(() => {
    function calc() {
      const diff = targetDate - new Date();

      if (diff <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          expired: true,
        };
      }

      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        expired: false,
      };
    }

    setTime(calc());

    const t = setInterval(() => {
      setTime(calc());
    }, 1000);

    return () => clearInterval(t);
  }, [targetDate]);

  return time;
}

function CountdownCard({ d }) {
  const time = useCountdown(d.date);

  const pct = Math.round(
    ((d.totalSeats - d.seats) / d.totalSeats) * 100
  );

  const urgent = d.seats <= 15;

  return (
    <div
      style={{
        background: 'var(--white)',
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
        border: '1.5px solid var(--graylt)',
        boxShadow: urgent
          ? '0 0 0 2px rgba(220,53,69,0.3), var(--shadow)'
          : 'var(--shadow)',
        transition: 'all 0.3s',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = urgent
          ? '0 0 0 2px rgba(220,53,69,0.3), var(--shadow-lg)'
          : 'var(--shadow-lg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = urgent
          ? '0 0 0 2px rgba(220,53,69,0.3), var(--shadow)'
          : 'var(--shadow)';
      }}
    >
      {urgent && (
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            background: '#dc3545',
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            padding: '4px 10px',
            borderRadius: 999,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            animation: 'urgentPulse 1.5s ease infinite',
          }}
        >
          🔥 Almost Full
        </div>
      )}

      {/* Header */}
      <div
        style={{
          background: d.color,
          padding: '22px 24px',
        }}
      >
        <span
          style={{
            background: 'rgba(245,197,24,0.2)',
            color: 'var(--yellow)',
            fontSize: 10,
            fontWeight: 800,
            padding: '4px 12px',
            borderRadius: 999,
            letterSpacing: '1px',
            textTransform: 'uppercase',
          }}
        >
          {d.badge}
        </span>

        <h3
          style={{
            fontFamily: 'Playfair Display, serif',
            color: '#fff',
            fontSize: 20,
            marginTop: 10,
          }}
        >
          {d.university}
        </h3>

        <p
          style={{
            color: 'rgba(255,255,255,0.65)',
            fontSize: 13,
            marginTop: 4,
          }}
        >
          {d.program}
        </p>
      </div>

      {/* Content */}
      <div style={{ padding: '24px' }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--gray)',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            marginBottom: 14,
          }}
        >
          {time.expired
            ? 'Admissions Closed'
            : 'Deadline Closes In'}
        </p>

        {!time.expired ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4,1fr)',
              gap: 8,
              marginBottom: 20,
            }}
          >
            {[
              { val: time.days, label: 'Days' },
              { val: time.hours, label: 'Hrs' },
              { val: time.minutes, label: 'Min' },
              { val: time.seconds, label: 'Sec' },
            ].map(({ val, label }) => (
              <div
                key={label}
                style={{
                  background: 'var(--navy)',
                  borderRadius: 10,
                  padding: '12px 6px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: 22,
                    fontWeight: 900,
                    color: 'var(--yellow)',
                    lineHeight: 1,
                  }}
                >
                  {String(val).padStart(2, '0')}
                </div>

                <div
                  style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.5)',
                    marginTop: 4,
                    fontWeight: 600,
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              background: 'var(--offwhite)',
              borderRadius: 10,
              padding: '16px',
              textAlign: 'center',
              marginBottom: 20,
            }}
          >
            <p
              style={{
                color: 'var(--gray)',
                fontSize: 14,
              }}
            >
              Next batch opening soon
            </p>
          </div>
        )}

        {/* Seat Availability */}
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: 13,
                color: 'var(--gray)',
                fontWeight: 600,
              }}
            >
              Seats Available
            </span>

            <span
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: urgent
                  ? '#dc3545'
                  : 'var(--navy)',
              }}
            >
              {d.seats} of {d.totalSeats} left
            </span>
          </div>

          <div
            style={{
              height: 6,
              background: 'var(--graylt)',
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 3,
                width: `${pct}%`,
                background: urgent
                  ? 'linear-gradient(90deg, #dc3545, #ff6b6b)'
                  : 'linear-gradient(90deg, var(--navy), var(--navy2))',
                transition: 'width 0.5s ease',
              }}
            />
          </div>
        </div>
      <a
        href="#contact"
        style={{
          display: 'block',
          textAlign: 'center',
          background: 'var(--navy)',
          color: 'var(--yellow)',
          padding: '13px',
          borderRadius: 10,
          fontWeight: 700,
          fontSize: 14,
          transition: 'all 0.25s',
          pointerEvents: 'auto',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--navy2)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--navy)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
>
        {time.expired ? 'Join Waitlist →' : 'Reserve My Seat →'}
      </a>

    </div>
    </div >
  );
}

export default function CountdownTimers() {
  return (
    <section
      id="deadlines"
      style={{
        padding: '100px 6%',
        background: 'var(--offwhite)',
      }}
    >
      <RevealBox
        style={{
          textAlign: 'center',
          maxWidth: 640,
          margin: '0 auto 56px',
        }}
      >
        <SectionLabel>Act Fast</SectionLabel>

        <SectionTitle
          style={{
            maxWidth: '100%',
            textAlign: 'center',
          }}
        >
          Admission{' '}
          <em style={{ fontStyle: 'italic' }}>
            Deadlines
          </em>{' '}
          Are Live
        </SectionTitle>

        <p
          style={{
            fontSize: 17,
            color: 'var(--gray)',
            marginTop: 14,
            lineHeight: 1.7,
          }}
        >
          Seats are limited and fill fast. Don&apos;t miss your
          intake window — secure your spot today.
        </p>
      </RevealBox>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 24,
        }}
      >
        {deadlines.map((d, i) => (
          <RevealBox
            key={d.university}
            delay={i * 0.07}
          >
            <CountdownCard d={d} />
          </RevealBox>
        ))}
      </div>

      <style>{`
        @keyframes urgentPulse {
          0%,100% {
            opacity: 1;
          }

          50% {
            opacity: 0.6;
          }
        }
      `}</style>
    </section>
  );
} 